import styled, { CSSObject } from '@emotion/styled'
import {
  useCartMatch,
  useConsumerCart,
  useModal,
  useQueryParams,
  useShop
} from 'shop/hooks'
import { StyledHTMLElement } from 'shop/theme/types'
import { useTheme } from '@emotion/react'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { HeaderLoader } from './Loaders'
import { Description } from '../../Description'
import { Message, MessagesModal } from '../../Messages'
import { findDefaultFulfillment } from 'shop/utils/common'
import { FulfillmentType } from 'shop/types'
import { getFulfillmentDetails } from '../Fulfillment/utils'
import { DescriptionType } from 'shop/components/Description/types'
import { hideScrollbar } from 'shop/components/common'
import { Header } from '../commonStyles'

export const ShopDetails = () => {
  const theme = useTheme()
  const { openModal, isModalOpen, fulfillmentModal, closeModal } = useModal()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const { currentPartnerStore, partner } = useShop()
  const { cart } = useConsumerCart()
  const { isCartMatch } = useCartMatch()
  const { getQueryParam } = useQueryParams()
  const [shouldShowFulfillmentModal, setShouldShowFulfillmentModal] =
    useState(false)
  const [showMessagesModal, setShowMessagesModal] = useState(false)

  const storeName = currentPartnerStore?.name
  const messages = partner?.content.messages
  const storeDescription = currentPartnerStore?.description

  const queryParamFulfillmentType =
    (getQueryParam('fulfillment_type').toUpperCase() as FulfillmentType) ||
    undefined

  const defaultFulfillmentInfo =
    findDefaultFulfillment(
      currentPartnerStore?.orderFulfillmentTypes,
      queryParamFulfillmentType
    ) ?? null

  const cartFulfillment = cart?.fulfillment || null

  const { orderType } = getFulfillmentDetails(
    cartFulfillment,
    defaultFulfillmentInfo,
    isCartMatch
  )

  // returns Sameday messages for orderType ASAP
  const messageData = useMemo(() => {
    if (!messages || !Array.isArray(messages)) return null

    const filteredMessages = messages
      .filter((message) => {
        const isRelevantMessageType =
          message.messageType === 'ANNOUNCEMENT' ||
          message.messageType === 'WARNING'
        const isRelevantOrderType =
          orderType === 'ASAP'
            ? message.orderType === 'SAMEDAY'
            : message.orderType === orderType

        return isRelevantMessageType && isRelevantOrderType
      })
      .sort((a, b) => {
        if (a.messageType < b.messageType) {
          return 1
        }
        if (a.messageType > b.messageType) {
          return -1
        }
        return 0
      })

    return filteredMessages.length > 0 ? filteredMessages : null
  }, [messages, orderType])

  const handleFulfillmentModalClose = () => {
    setShouldShowFulfillmentModal(false)
    closeModal('fulfillment')
  }

  useEffect(() => {
    const queryParam = new URLSearchParams(search)
    // If fulfillment query param from login modal flow exists, remove it
    if (queryParam.get('fulfillment') && !isModalOpen('fulfillment')) {
      setShouldShowFulfillmentModal(true)
      openModal('fulfillment')
      navigate({
        pathname: pathname
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Root data-testid='shop-details' hasMessages={!!messageData?.length}>
      {isModalOpen('fulfillment') &&
        shouldShowFulfillmentModal &&
        fulfillmentModal({
          onClose: handleFulfillmentModalClose
        })}
      {!partner?.name || !storeName ? (
        <HeaderLoader theme={theme} />
      ) : (
        <HeaderContainer>
          <Header>
            {partner?.name && <Merchant>{partner?.name}</Merchant>}{' '}
            {storeName && <Store>{storeName}</Store>}
          </Header>
          {!!storeDescription?.length && (
            <Description
              description={storeDescription}
              type={DescriptionType.SHOP}
            />
          )}
        </HeaderContainer>
      )}

      <MessagesContainer data-testid='messages-container'>
        {messageData && (
          <>
            {messageData.map((message) => (
              <Message
                {...message}
                key={`${message.orderType}-${message.messageType}`}
                handleOpenModal={() => setShowMessagesModal(true)}
                isMessagesModal={false}
              />
            ))}
            {showMessagesModal && (
              <MessagesModal
                messages={messageData}
                handleCloseModal={() => setShowMessagesModal(false)}
              />
            )}
          </>
        )}
      </MessagesContainer>
    </Root>
  )
}

const Root = styled.div<StyledHTMLElement & { hasMessages: boolean }>(
  ({ theme, hasMessages }) => ({
    display: 'flex',
    flexFlow: 'column',
    borderRadius: theme.borderRadius,
    gap: hasMessages ? '12px' : '0',
    [theme.mediaQueries.viewport7]: {
      border: '1px solid #D9D9D9',
      backgroundColor: 'white',
      padding: '12px 0'
    }
  })
)

const HeaderContainer = styled.div<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  flexFlow: 'column',
  gap: '4px',
  padding: '0 16px',
  [theme.mediaQueries.viewport7]: {
    gap: '12px'
  }
}))

const MessagesContainer = styled.div<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  gap: '8px',
  overflowX: 'scroll',
  padding: '0 16px',
  ...(hideScrollbar as any),

  [theme.mediaQueries.viewport7]: {
    display: 'flex',
    gap: '8px'
  }
}))

const Merchant = styled.span<StyledHTMLElement>(
  (): CSSObject => ({
    fontWeight: 600,
    textTransform: 'uppercase'
  })
)

const Store = styled.span<StyledHTMLElement>(
  (): CSSObject => ({
    fontWeight: 400
  })
)

export default ShopDetails
